/**
 * Randomly shuffle passed in array
 */
export function shuffle<T>(array: Array<T>): Array<T> {
  let rand
  let tmp
  let len = array.length
  const ret = array.slice()

  while (len) {
    rand = Math.floor(Math.random() * len--)
    tmp = ret[len]
    ret[len] = ret[rand]
    ret[rand] = tmp
  }

  return ret
}

/**
 * reduces an array of objects by an given key
 *
 * e.g. reduceBy(
 *   [
 *     { teamId: 1, name: 'dandy' },
 *     { teamId: 1, name: 'rui' },
 *     { teamId: 1, name: 'katie' },
 *     { teamId: 2, name: 'desi' },
 *     { teamId: 2, name: 'mike' },
 *   ],
 *   (item) => item.teamId,
 *   (item) => item.name)
 *
 * returns Map(
 *  1 => ['dandy', 'rui', 'katie'],
 *  2 => ['desi', 'mike'])
 */
export const reduceBy = <T, K, V>(
  entries: Array<T>,
  getKey: (entry: T) => K,
  getValue: (entry: T) => V,
): Map<K, Array<V>> =>
  entries.reduce((map, entry) => {
    const key = getKey(entry)
    const value = getValue(entry)
    if (map.has(key)) {
      map.get(key)?.push(value)
    } else {
      map.set(key, [value])
    }
    return map
  }, new Map<K, Array<V>>())

/**
 * return flattened distinct array of Map values
 *
 * e.g. flattenMapValues(Map(1 => ['dandy', 'rui', 'katie'], 2 => ['desi', 'mike', 'dandy']))
 *
 * returns ['dandy', 'rui', 'katie', 'desi', 'mike']
 */
export const flattenMapValues = <K, V>(map: Map<K, Array<V>>): Array<V> => [...new Set([...map.values()].flat())]

/**
 * remove html tags & entities from a text and replace with plain text characters
 */
export const replaceHTML = (text: string): string => {
  const listItemTag = /<li>/gi
  const endOfList = /<\/\s*ul>/gi
  const htmlTags = /(<([^>]+)>)/gi

  return text
    .replace(listItemTag, '\n· ')
    .replace(endOfList, '\n')
    .replace(/<br\s*\/*>/gi, '\n')
    .replace(/<p\s*>/gi, '\n')
    .replace(htmlTags, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .trim()
}

export const assertNever = (value: never): never => {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`)
}

export const toKebabCase = (str: string) =>
  str &&
  str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)!
    .map((x) => x.toLowerCase())
    .join('-')

/**
 * Reduces an array to unique elements (copied form legacy types and utils)
 *
 * @example
 * uniquify([2,5,2,2,4]])
 * // => [2, 5, 4]
 */
export function uniquify<T>(array: Array<T>): Array<T> {
  return array.filter((value, index) => array.indexOf(value) === index)
}

/**
 * Removes nullish values (null and undefined) from an array and ensures the return type
 *
 * @example
 * removeNullish([0, false, undefined, '', null, 'pizza'])
 * // => [0, false, '', 'pizza']
 */
export function removeNullish<T>(array: Array<T | undefined | null>): Array<T> {
  return array.filter((value): value is T => value !== undefined && value !== null)
}

export function slugify(name: string): string {
  return name.replace(/[\W_]+/g, '-').toLowerCase()
}
